import { PH_FEATURES } from './generated';

export namespace PostHog_Features {
    export type Name = keyof typeof PH_FEATURES;
    export type Variant<T extends PostHog_Features.Name> = (typeof PH_FEATURES)[T][number];
    /** A variant per flag, for the flags that have one forced. */
    export type Overrides = { [Flag in Name]?: Variant<Flag> };
}

export const PostHog_OVERRIDES: PostHog_Features.Overrides = {
    'plp-extra-filters': 'enabled',
    'plp-filter-sidebar': 'sidebar',
    // 'simplified-checkout': 'simplified',
};

/** Mirrors the flags forced from the PostHog toolbar. The toolbar keeps them in the browser's
 * storage, which the server never sees; without this cookie the server renders the bucketed
 * variant and the forced one only appears after the browser takes over. */
export const PostHog_OVERRIDE_COOKIE = 'ph-flag-overrides';

/** The valid overrides in `raw`: a map of flag to variant, or that map as JSON. Unknown flags and
 * variants a flag does not have are dropped, so a hand-edited cookie cannot force anything else. */
export function PostHog_parseOverrides(raw: unknown): PostHog_Features.Overrides {
    const value = typeof raw === 'string' ? parseJson(raw) : raw;
    const overrides: PostHog_Features.Overrides = {};
    if (!value || typeof value !== 'object') return overrides;

    for (const [name, variant] of Object.entries(value)) {
        if (isFlagName(name) && isVariantOf(name, variant))
            Object.assign(overrides, { [name]: variant });
    }
    return overrides;
}

function parseJson(raw: string): unknown {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function isFlagName(name: string): name is PostHog_Features.Name {
    return Object.hasOwn(PH_FEATURES, name);
}

function isVariantOf<T extends PostHog_Features.Name>(
    name: T,
    variant: unknown,
): variant is PostHog_Features.Variant<T> {
    const variants: readonly unknown[] = PH_FEATURES[name];
    return variants.includes(variant);
}
